import { useCallback } from 'react';
import {
  pinDefault,
  pinnedInstance,
  unpinDefault,
  type EquipmentDefault,
} from '@jackedlogs/shared';

import { useProfileListOverlay } from './_useProfileListOverlay';

// EQ-1-PIN-DEFAULT — the ★'s half of "which machine wins here".
//
// Deliberately the same shape as `_useLocationUnavailable`: same optimistic
// overlay, same single existing `patchProfile` door narrowed to one field, same
// send-absent-when-empty rule. They are siblings answering opposite questions,
// so the day one grows a queue or a retry the other should grow the same one,
// and that is easier to see when they read alike.
//
// ⛔ Two FIELDS though, never one array. `hasAvailableInstance` can sink an
// exercise row when everything is declared missing; if an election lived in the
// same list, electing a machine could sink the row that offers it.

export type EquipmentDefaultControl = {
  /**
   * Where an election would be keyed, or null when none can be.
   *
   * ⚠️ Null withholds the whole ★, exactly as it withholds the ✕: a "this one
   * by default" with no "here" has no key to be stored under. Omitted rather
   * than shown-and-inert.
   */
  place: string | null;
  /** What is elected for one exercise here: slug, `null` = the Default,
   *  `undefined` = nothing elected. */
  pinnedFor: (exercise: string) => string | null | undefined;
  /**
   * Tap the ★ on a row.
   *
   * Re-tapping the row already in force UN-elects, restoring the resolver's own
   * rules. That third state has to exist: a ★ that only ever moved between two
   * machines would give the athlete no way back to being asked, and being asked
   * is the right answer when they genuinely alternate.
   */
  toggle: (exercise: string, instance: string | null) => void;
};

export function useEquipmentDefault({
  visible,
  place,
  elected,
  patchProfile,
  notify,
  t,
}: {
  /** The host surface's visibility — closing it drops the optimistic overlay. */
  visible: boolean;
  place: string | null;
  /** `Profile.equipmentDefault` as the host last read it. */
  elected: EquipmentDefault | undefined;
  patchProfile: (patch: {
    equipmentDefault?: EquipmentDefault;
  }) => Promise<void>;
  notify: (title: string, body: string) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}): EquipmentDefaultControl {
  // Mechanics shared with the ✕'s hook — optimistic write, roll back on
  // failure, empty list sent as absent. Extracted rather than mirrored: two
  // copies of "write optimistically" stop agreeing the day one grows a retry.
  const { list, write } = useProfileListOverlay({
    visible,
    place,
    source: elected,
    field: 'equipmentDefault' as const,
    patchProfile,
    notify,
    errorTitle: () =>
      t('swap.pinErrorTitle', { defaultValue: 'Not saved' }),
    errorBody: () =>
      t('swap.pinErrorBody', {
        defaultValue: 'The default machine here is unchanged.',
      }),
  });

  const pinnedFor = useCallback(
    (exercise: string) => pinnedInstance(list, place, exercise),
    [list, place],
  );

  const toggle = useCallback(
    (exercise: string, instance: string | null) => {
      if (place === null) return;
      const current = pinnedInstance(list, place, exercise);
      const next =
        current === instance
          ? unpinDefault(list, place, exercise)
          : pinDefault(list, {
              place,
              exercise,
              instance,
              at: new Date().toISOString(),
            });
      write(next);
    },
    [list, place, write],
  );

  return { place, pinnedFor, toggle };
}
